/**
 * Preview sandbox origin policy.
 *
 * This module is intentionally free of WASM/build-time dependencies and touches
 * only `window.location` / `window.__SANDBOX_ORIGIN__`, so the production
 * fail-closed behavior can be unit-tested directly without a browser build.
 *
 * Production builds rewrite only SANDBOX_ORIGIN_CONFIG below. The resolver
 * logic stays single-source so the preview security boundary cannot drift.
 */

const SANDBOX_ORIGIN_CONFIG = {
  "allowLocalFallback": false,
  "localHostnames": [],
  "localSourcePort": "",
  "localSandboxPort": "",
  "productionParentHosts": [
    "playground.frontmyend.com",
    "staging.internal.playground.frontmyend.com",
    "dev.internal.playground.frontmyend.com"
  ],
  "productionParentHostSuffixes": [],
  "productionPreviewOrigin": "https://preview.frontmyend.com"
};

export function isLocalDevelopmentOrigin() {
  const { protocol, hostname } = window.location;
  return SANDBOX_ORIGIN_CONFIG.allowLocalFallback && (
    protocol === 'file:' || SANDBOX_ORIGIN_CONFIG.localHostnames.includes(hostname)
  );
}

function isConfiguredProductionParentHost(hostname) {
  if (SANDBOX_ORIGIN_CONFIG.productionParentHosts.includes(hostname)) {
    return true;
  }

  return SANDBOX_ORIGIN_CONFIG.productionParentHostSuffixes.some((suffix) => hostname.endsWith(suffix));
}

export function getSandboxUrl() {
  const parent = encodeURIComponent(window.location.origin);

  // 1. Explicit build-time config (highest priority)
  if (window.__SANDBOX_ORIGIN__) {
    return `${window.__SANDBOX_ORIGIN__}/sandbox?parent=${parent}`;
  }

  const loc = window.location;

  // 2. Development: port swap on configured hostnames.
  if (SANDBOX_ORIGIN_CONFIG.allowLocalFallback && SANDBOX_ORIGIN_CONFIG.localHostnames.includes(loc.hostname)) {
    if (loc.port === SANDBOX_ORIGIN_CONFIG.localSourcePort) {
      return `${loc.protocol}//${loc.hostname}:${SANDBOX_ORIGIN_CONFIG.localSandboxPort}/sandbox.html?parent=${parent}`;
    }
  }

  // 3. Canonical production deployment. Non-standard deployments must set
  // window.__SANDBOX_ORIGIN__ explicitly so production does not guess origins.
  const isProductionOrPreview = loc.protocol === 'https:' &&
    !loc.port &&
    isConfiguredProductionParentHost(loc.hostname);

  if (isProductionOrPreview) {
    return `${SANDBOX_ORIGIN_CONFIG.productionPreviewOrigin}/sandbox?parent=${parent}`;
  }

  // 4. No isolation available
  return null;
}
